<template>
  <div :class="{ 'app-container': !fixedTarget }">
    <el-card v-if="!fixedTarget" class="box-card">
      <el-form :inline="true" size="small">
        <el-form-item label="配置方式">
          <el-radio-group v-model="targetType" :disabled="saving" @change="onTargetTypeChange">
            <el-radio-button label="scene">场景</el-radio-button>
            <el-radio-button label="category">分类下场景（批量）</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="targetType === 'scene' ? '场景' : '所属分类'">
          <el-select v-model="targetId" filterable :placeholder="targetType === 'scene' ? '请选择场景' : '请选择分类，统一设置其下场景权限'" style="width:320px" :loading="targetLoading" :disabled="saving || targetLoading" @change="loadAuth">
            <el-option v-for="o in targetOptions" :key="o.id" :label="o.label" :value="o.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <el-alert v-if="targetError" :title="targetError" type="error" :closable="false">
        <el-button type="text" @click="loadTargetOptions">重新加载对象</el-button>
      </el-alert>
      <el-alert v-if="targetId && targetsLoaded && !targetRecognized" title="所选对象已不可用，请重新选择。" type="warning" :closable="false" />
    </el-card>

    <template v-if="targetId">
      <el-alert :title="targetType === 'category' ? '以下配置对所属分类下的场景批量生效；分类导航不受影响。请确认受众范围后保存。' : '按客户等级、技术等级、注册角色和内部员工配置本场景受众，所属分类的批量授权仍会继承。分类导航不受权限影响。'" type="info" :closable="false" class="mt10" />
      <el-alert v-if="optionsError" :title="optionsError" type="error" :closable="false" class="mt10">
        <el-button type="text" @click="loadAudiences">重新加载受众选项</el-button>
      </el-alert>
      <el-alert v-if="authError" :title="authError" type="error" :closable="false" class="mt10">
        <el-button type="text" @click="loadAuth">重新加载权限</el-button>
      </el-alert>

      <el-card class="box-card mt10" v-loading="loading || optionsLoading">
        <div slot="header">受众范围</div>
        <div v-for="group in audienceGroups" :key="group.key" class="audience-group">
          <div class="group-label">
            {{ group.label }}
            <span v-if="group.key === 'customer' || group.key === 'technical'" class="group-hint">仅匹配勾选的等级</span>
            <span v-if="group.key === 'internal'" class="group-hint">勾选后覆盖所有内部员工</span>
          </div>
          <div class="audience-options">
            <button
              v-for="option in group.options"
              :key="option.id"
              type="button"
              class="audience-chip"
              :class="{ active: selectedFor(group.key).includes(option.id) }"
              :aria-pressed="selectedFor(group.key).includes(option.id) ? 'true' : 'false'"
              :disabled="selectionDisabled"
              @click="toggleAudience(group.key, option.id)"
            >
              {{ option.name }}
              <i v-if="selectedFor(group.key).includes(option.id)" class="el-icon-success selected-mark" aria-hidden="true" />
            </button>
            <span v-if="!group.options.length" class="empty-hint">{{ audiencesLoaded ? group.emptyText : '选项加载中' }}</span>
          </div>
        </div>
      </el-card>

      <el-card v-if="invalidSelections.length" class="box-card mt10">
        <el-alert title="存在已失效或无法识别的受众权限。本页不会自动移除，请核实后明确移除并重新选择，才能保存。" type="warning" :closable="false" class="mb10" />
        <div v-for="entry in invalidSelections" :key="entry.key" class="invalid-row">
          <span>{{ entry.label }}</span>
          <el-button type="text" class="danger-text" :disabled="selectionDisabled" @click="removeInvalidSelection(entry)" v-hasPermi="editPermissions">移除此项</el-button>
        </div>
      </el-card>

      <div class="mt10 save-bar">
        <el-button :disabled="saving" @click="loadAuth">重新读取场景权限</el-button>
        <el-button v-if="showSave" type="primary" :loading="saving" :disabled="saveDisabled" @click="handleSave" v-hasPermi="editPermissions">保存场景权限</el-button>
      </div>
    </template>
    <el-empty v-else description="请选择场景，或选择分类批量设置其下场景权限" />
  </div>
</template>

<script>
import { listRows, treeRows } from '@/utils/ai-train-data'
import aiTrainAuthApi from '@/api/aiTrain/auth'
import aiTrainSceneApi from '@/api/aiTrain/scene'
import aiTrainCategoryApi from '@/api/aiTrain/category'

const AUTH_TYPES = { customer: 4, technical: 5, internal: 6, registration: 7 }
const GROUP_FIELDS = { customer: 'customerLevels', technical: 'technicalLevels', internal: 'internalStaff', registration: 'registrationRoles' }
function validId(value) {
  return Number.isSafeInteger(Number(value)) && Number(value) > 0
}

export default {
  name: 'AiTrainAudienceAuthorization',
  props: {
    editPermission: { type: String, default: 'aiTrain:auth:edit' },
    fixedTarget: { type: Object, default: null },
    showSave: { type: Boolean, default: true }
  },
  data() {
    return {
      targetType: 'scene', targetId: null, targetOptions: [],
      targetLoading: false, targetsLoaded: false, targetError: '', targetSequence: 0,
      audiences: { customerLevels: [], technicalLevels: [], registrationRoles: [], internalStaff: [] },
      audiencesLoaded: false, optionsLoading: false, optionsError: '', optionsSequence: 0,
      selected: { customer: [], technical: [], registration: [], internal: [] },
      loading: false, saving: false, authLoaded: false, authError: '',
      authSequence: 0, loadedTargetKey: '', initialAuthCount: 0, initialSelectionKey: ''
    }
  },
  computed: {
    editPermissions() { return ['aiTrain:auth:edit', 'aiTrain:permission:edit'] },
    canEdit() {
      const permissions = this.$store && this.$store.getters && this.$store.getters.permissions
      return Array.isArray(permissions) && (permissions.includes('*:*:*') || this.editPermissions.some(code => permissions.includes(code)))
    },
    targetKey() { return this.targetType + ':' + this.targetId },
    targetRecognized() { return this.targetsLoaded && this.targetOptions.some(o => o.id === this.targetId) },
    selectionDisabled() {
      return !this.canEdit || !this.audiencesLoaded || !this.authLoaded || !this.targetRecognized || this.loadedTargetKey !== this.targetKey || this.loading || this.saving
    },
    saveDisabled() { return this.selectionDisabled || this.invalidSelections.length > 0 },
    selectionKey() { return JSON.stringify(Object.keys(AUTH_TYPES).map(group => [group, this.selectedFor(group).slice().sort((a, b) => a - b)])) },
    isDirty() { return this.authLoaded && this.selectionKey !== this.initialSelectionKey },
    audienceGroups() {
      return [
        { key: 'customer', label: '客户等级', options: this.audiences.customerLevels, emptyText: '暂无客户等级，请先在客户等级管理中配置' },
        { key: 'technical', label: '技术等级', options: this.audiences.technicalLevels, emptyText: '暂无技术等级，请先在技术等级管理中配置' },
        { key: 'registration', label: '注册角色', options: this.audiences.registrationRoles.map(r => ({ id: r.id, name: r.roleName })), emptyText: '暂无注册角色，请先在系统角色管理中配置注册角色' },
        { key: 'internal', label: '内部员工', options: this.audiences.internalStaff, emptyText: '暂无可选内部员工范围' }
      ]
    },
    invalidSelections() {
      if (!this.audiencesLoaded) return []
      const result = []
      this.audienceGroups.forEach(group => {
        const available = group.options.map(o => o.id)
        this.selectedFor(group.key).filter(id => !available.includes(id)).forEach(id => result.push({
          key: group.key + '-' + id, group: group.key, id,
          label: '原' + group.label + '权限 ' + id + ' 已失效或无法识别'
        }))
      })
      return result
    }
  },
  created() {
    if (this.fixedTarget) {
      const target = this.fixedTarget
      if (!validId(target.id) || !['scene', 'category'].includes(target.type)) {
        this.targetError = '场景权限对象不可用，请关闭后重新打开'
        return
      }
      this.targetType = target.type
      this.targetId = Number(target.id)
      this.targetOptions = [{ id: this.targetId, label: target.name }]
      this.targetsLoaded = true
      this.loadAuth()
      this.loadAudiences()
      return
    }
    const q = this.$route.query
    this.targetType = q.targetType === 'category' ? 'category' : 'scene'
    this.loadTargetOptions().then(() => {
      if (!q.targetId || !this.targetsLoaded) return
      const requestedId = Number(q.targetId)
      if (this.targetOptions.some(o => o.id === requestedId)) {
        this.targetId = requestedId
        this.loadAuth()
      } else this.targetError = '所选权限对象不在当前可用列表中，请重新选择。'
    })
    this.loadAudiences()
  },
  watch: {
    saving(value) { this.$emit('busy-change', value) }
  },
  beforeDestroy() { ++this.authSequence; ++this.optionsSequence; ++this.targetSequence },
  methods: {
    selectedFor(group) { return this.selected[group] || [] },
    toggleAudience(group, id) {
      if (this.selectionDisabled) return
      const values = this.selectedFor(group)
      const index = values.indexOf(id)
      if (index < 0) values.push(id)
      else values.splice(index, 1)
    },
    onTargetTypeChange() {
      this.targetId = null
      ++this.authSequence
      this.resetSelection()
      this.loadTargetOptions()
    },
    loadTargetOptions() {
      const sequence = ++this.targetSequence
      const type = this.targetType
      this.targetLoading = true
      this.targetsLoaded = false
      this.targetError = ''
      const loader = type === 'scene'
        ? aiTrainSceneApi.fetchList({ pageNo: 1, pageSize: 500 }).then(res => listRows((res && res.data) || res || {}).map(r => ({ id: Number(r.id), label: r.title })))
        : Promise.all([1, 2, 3].map(t => aiTrainCategoryApi.fetchTree(t).then(res => treeRows((res && res.data) || res)))).then(results => {
          const out = []
          const flatten = (tree, prefix) => {
            tree.forEach(node => {
              const label = prefix ? prefix + ' / ' + node.name : node.name
              out.push({ id: Number(node.id), label })
              if (node.children && node.children.length) flatten(node.children, label)
            })
          }
          results.forEach(tree => flatten(tree, ''))
          return out
        })
      return loader.then(options => {
        if (sequence !== this.targetSequence || type !== this.targetType) return false
        if (options.some(o => !validId(o.id) || !o.label)) throw new Error('权限对象选项异常')
        this.targetOptions = options
        this.targetsLoaded = true
        return true
      }).catch(() => {
        if (sequence === this.targetSequence) { this.targetOptions = []; this.targetError = '权限对象加载失败，请重新加载后选择。' }
        return false
      }).finally(() => { if (sequence === this.targetSequence) this.targetLoading = false })
    },
    loadAudiences() {
      const sequence = ++this.optionsSequence
      this.optionsLoading = true
      this.optionsError = ''
      this.audiencesLoaded = false
      return aiTrainAuthApi.fetchAudiences().then(res => {
        if (sequence !== this.optionsSequence) return false
        const data = (res && res.data) || res || {}
        const next = {}
        for (const field of Object.values(GROUP_FIELDS)) {
          const rows = listRows(data[field], false)
          if (rows.some(r => !validId(r.id) || !(field === 'registrationRoles' ? r.roleName : r.name))) throw new Error('受众选项异常')
          if (field === 'internalStaff' && rows.some(r => Number(r.id) !== 1)) throw new Error('内部员工选项异常')
          next[field] = rows.map(r => ({ ...r, id: Number(r.id) }))
        }
        this.audiences = next
        this.audiencesLoaded = true
        return true
      }).catch(() => {
        if (sequence === this.optionsSequence) this.optionsError = '受众选项加载失败，请重新加载后再保存权限。'
        return false
      }).finally(() => { if (sequence === this.optionsSequence) this.optionsLoading = false })
    },
    resetSelection() {
      this.selected = { customer: [], technical: [], registration: [], internal: [] }
      this.authLoaded = false
      this.authError = ''
      this.loadedTargetKey = ''
      this.initialAuthCount = 0
      this.loading = false
    },
    loadAuth() {
      const sequence = ++this.authSequence
      this.resetSelection()
      if (!this.targetId || !this.targetRecognized) return Promise.resolve(false)
      const key = this.targetKey
      const query = this.targetType === 'scene' ? { sceneId: this.targetId } : { categoryId: this.targetId }
      this.loading = true
      return aiTrainAuthApi.fetchList(query).then(res => {
        if (sequence !== this.authSequence || key !== this.targetKey) return false
        const rows = listRows((res && res.data) || res || [])
        if (rows.some(r => !Object.values(AUTH_TYPES).includes(r.authType) || !validId(r.targetId))) throw new Error('返回的权限数据不是当前四组受众规则')
        rows.forEach(r => {
          const group = Object.keys(AUTH_TYPES).find(k => AUTH_TYPES[k] === r.authType)
          const id = Number(r.targetId)
          if (!this.selected[group].includes(id)) this.selected[group].push(id)
        })
        this.initialAuthCount = rows.length
        this.loadedTargetKey = key
        this.authLoaded = true
        this.initialSelectionKey = this.selectionKey
        return true
      }).catch(() => {
        if (sequence === this.authSequence) this.authError = '当前受众权限加载失败，不能保存空白选项，请重新加载或稍后刷新。'
        return false
      }).finally(() => { if (sequence === this.authSequence) this.loading = false })
    },
    removeInvalidSelection(entry) {
      if (this.selectionDisabled) return
      this.selected[entry.group] = this.selectedFor(entry.group).filter(id => id !== entry.id)
    },
    handleSave() {
      if (this.selectionDisabled) { this.$message.warning('请等待权限对象、受众选项及当前权限完整加载后保存'); return }
      if (this.invalidSelections.length) { this.$message.warning('请先明确移除无法识别的受众权限，并重新选择'); return }
      const items = []
      this.audienceGroups.forEach(group => this.selectedFor(group.key).forEach(id => items.push({ authType: AUTH_TYPES[group.key], targetId: id })))
      const payload = this.targetType === 'scene' ? { sceneId: this.targetId, categoryId: null, items } : { sceneId: null, categoryId: this.targetId, items }
      if (!items.length && this.initialAuthCount > 0) {
        const key = this.targetKey
        return this.$common.confirm('确认撤销所选场景范围的全部受众权限？原受众将失去这些场景的访问权限，分类导航不受影响。').then(() => {
          if (key !== this.targetKey || this.selectionDisabled) { this.$message.warning('权限对象已切换，请重新检查后保存'); return }
          return this.savePayload(payload)
        }).catch(() => false)
      } else return this.savePayload(payload)
    },
    savePayload(payload) {
      const key = payload.sceneId ? 'scene:' + payload.sceneId : 'category:' + payload.categoryId
      if (key !== this.targetKey || this.selectionDisabled || this.invalidSelections.length) return Promise.resolve(false)
      this.saving = true
      return aiTrainAuthApi.save(payload).then(() => this.loadAuth()).then(loaded => {
        if (loaded) this.$message.success('保存成功，已重新读取权限')
        else this.$message.warning('权限已保存，但重新读取失败，请重新加载确认')
        return loaded
      }).catch(() => false).finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.mt10 { margin-top: 10px; }
.mb10 { margin-bottom: 10px; }
.save-bar { text-align: right; }
.audience-group + .audience-group { margin-top: 22px; }
.group-label { margin-bottom: 12px; color: #606266; font-weight: 500; }
.group-hint { margin-left: 12px; color: #909399; font-size: 12px; font-weight: normal; }
.audience-options { display: flex; flex-wrap: wrap; gap: 12px; }
.audience-chip { position: relative; padding: 8px 16px; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; color: #606266; cursor: pointer; font: inherit; transition: background-color .2s, border-color .2s; }
.audience-chip:not(:disabled):hover { border-color: #409eff; color: #409eff; }
.audience-chip.active { background: #409eff; border-color: #409eff; color: #fff; }
.audience-chip:disabled { cursor: not-allowed; opacity: .65; }
.selected-mark { position: absolute; top: -7px; right: -7px; color: #67c23a; background: #fff; border-radius: 50%; font-size: 18px; }
.empty-hint { color: #909399; font-size: 13px; }
.danger-text { color: #f56c6c; }
.invalid-row { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #ebeef5; padding: 8px 0; }
</style>
